import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { SxProps } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import React, { useCallback, useState } from 'react';
import { GetEmailMarketingReportData } from 'services/ContactsService';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { NameIdObj } from 'common/models/GenericTypes';
import { BasicFilter } from 'common/models/Reporting/AnalyticsFilterData';
import MultipleGroupPicker from 'components/Pickers/MultipleGroupPicker';
import MultipleUserPicker from 'components/Pickers/MultipleUserPicker';

interface Props {
  description: string;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
}

const filterElementStyle: SxProps = { flex: '1 1 0', mr: 1 };

export default function EmailMarketingReport({ loadingHandler, errorHandler }: Props) {
  const [sourceType, setSourceType] = useState(1);
  const [status, setStatus] = useState(0);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

  const getDataCallback = useCallback(async () => {
    let sourceIds = '';
    if (sourceType === 3) sourceIds = selectedGroups.join(',');
    else if (sourceType === 4) sourceIds = selectedUsers.join(',');

    const f: BasicFilter = {
      userFilterID: sourceIds,
      userFilterTypeID: sourceType,
    };

    loadingHandler && loadingHandler(true);
    const res = await GetEmailMarketingReportData(f, status, errorHandler);
    if (res) {
      const m = moment();
      const filename = `EmailMarketing${m.format('YYYYMMDDhhmmss')}.xlsx`;
      const ws = XLSX.utils.json_to_sheet(res);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws);
      XLSX.writeFile(wb, filename);
    }
    loadingHandler && loadingHandler(false);
  }, [sourceType, selectedGroups, selectedUsers, loadingHandler, status, errorHandler]);

  const onGroupsChange = useCallback((groups: NameIdObj[]) => {
    setSelectedGroups(groups.map((u) => u.id));
  }, []);

  const onUsersChange = useCallback((users: NameIdObj[]) => {
    setSelectedUsers(users.map((u) => u.id));
  }, []);

  return (
    <>
      <Box display="flex">
        <TextField
          select
          label="Source Type"
          value={sourceType.toString()}
          onChange={({ target }) => setSourceType(+target.value)}
          sx={filterElementStyle}
        >
          <MenuItem value="1">All</MenuItem>
          <MenuItem value="3">Group</MenuItem>
          <MenuItem value="4">User</MenuItem>
        </TextField>
        <TextField
          select
          label="Status"
          value={status.toString()}
          onChange={({ target }) => setStatus(+target.value)}
          sx={filterElementStyle}
        >
          <MenuItem value="0">All</MenuItem>
          <MenuItem value="1">Unqualified</MenuItem>
          <MenuItem value="2">Qualified</MenuItem>
          <MenuItem value="3">Client</MenuItem>
          <MenuItem value="4">Supplier</MenuItem>
          <MenuItem value="7">Supplier - Sub Contractor</MenuItem>
          <MenuItem value="5">Billing</MenuItem>
          <MenuItem value="6">Archived</MenuItem>
          <MenuItem value="8">Referee</MenuItem>
          <MenuItem value="9">Prospect</MenuItem>
        </TextField>
        <Button variant="contained" color="success" onClick={getDataCallback}>
          Run Report
        </Button>
      </Box>
      <Box display={sourceType === 3 ? undefined : 'none'} mt={2}>
        <MultipleGroupPicker value={selectedGroups} onSelect={onGroupsChange} />
      </Box>
      <Box display={sourceType === 4 ? undefined : 'none'} mt={2}>
        <MultipleUserPicker includeInactiveUsers value={selectedUsers} onSelect={onUsersChange} />
      </Box>
    </>
  );
}
